import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import { PUZZEL_OIDC_LOGIN_FLAG } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  DropdownAndInputGroup,
  HorizontalContainer,
  ProfileDetails,
} from "features/Header/PuzzelTelephone/components/Logon/StyledLogonComponents";
import { useCallStatus } from "features/Header/PuzzelTelephone/provider/contexts/CallStatusContext";
import { usePuzzelExtendedStateInformation } from "features/Header/PuzzelTelephone/provider/contexts/ListenForCallStatusContext";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import {
  usePerformUserLogOnOff,
  useUserDetailsState,
} from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import {
  removePhoneNumberPrefix,
  validPhoneNumber,
} from "features/Header/PuzzelTelephone/utils";
import { FormEvent, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonButton } from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import { Select } from "shared/components/forms/Select";
import { DropsButton, Input } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { QueueHeadsetFill24Icon } from "shared/icons/QueueHeadsetFill24Icon";
import styled from "styled-components";

type PuzzelProfile = {
  groupProfile: boolean;
  id: string;
  name: string;
  number: number;
  phoneType: string;
  qualities: Array<unknown>;
  phoneNumber?: string;
  lastPhoneNumber?: string;
};

type SelectItem = {
  value: string;
  text: string;
};

// "Velg et alternativ" is 18 characters
const ProfileContainer = styled.div`
  button > :first-child > :first-child {
    transform: scale(0.8) translateY(-2px) translateX(-5px);
  }
  width: 250px;
`;

const Logon = () => {
  const {
    phoneNumber: nonPrefixedPhoneNumber,
    profileId,
    setPhoneNumber,
    setProfileId,
  } = useCallStatus();

  const { t } = useTranslation();
  const { enabled: oidcEnabled } = useFeatureFlag(PUZZEL_OIDC_LOGIN_FLAG);
  const userDetails = useUserDetailsState();
  const { userStateInformation } = usePuzzelExtendedStateInformation();
  const { phoneNumberPrefix, logout } = usePuzzelAuthenticationContext();
  const { userId } = useUserValidationState();
  const performUserLogOnOff = usePerformUserLogOnOff();

  const { currentProfile, profiles, isLoggedOn } = userDetails;
  const enableBtn = profileId && validPhoneNumber(nonPrefixedPhoneNumber);

  const items: SelectItem[] | undefined = useMemo(
    () =>
      profiles?.map((profile: PuzzelProfile) => ({
        text: profile.name,
        value: profile.id,
      })),
    [profiles],
  );

  const logOn = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logon",
        },
        payload: {
          phoneNumber: phoneNumberPrefix + nonPrefixedPhoneNumber,
          profileId,
        },
      });
    },
    [
      performUserLogOnOff,
      userId,
      phoneNumberPrefix,
      nonPrefixedPhoneNumber,
      profileId,
    ],
  );

  const logOff = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logoff",
        },
        payload: {},
      });
    },
    [performUserLogOnOff, userId],
  );

  useEffect(() => {
    if (currentProfile?.id) {
      setProfileId(currentProfile?.id);
    }
  }, [currentProfile?.id]);

  if (isLoggedOn === null) {
    return <SkeletonButton />;
  }

  if (
    !isLoggedOn ||
    userStateInformation?.contactCentreStatus === "LoggedOff"
  ) {
    return (
      <HorizontalContainer onSubmit={logOn}>
        <DropdownAndInputGroup>
          <ProfileContainer>
            <Select
              name="profileId"
              label="Telefonkø"
              value={profileId}
              onChange={(e) => {
                const { value } = e.target;
                if (typeof value !== "string") return;
                setProfileId(value);
              }}
            >
              {items?.map((item) => (
                <option value={item.value} key={item.value}>
                  {item.text}
                </option>
              ))}
            </Select>
          </ProfileContainer>

          <Input
            type="text"
            width="250px"
            onChange={(event) => {
              setPhoneNumber(event.target.value);
            }}
            value={nonPrefixedPhoneNumber}
            label="Telefonnummer"
          />
        </DropdownAndInputGroup>
        <DropsButton
          size="md"
          variant="primary"
          type="submit"
          disabled={!enableBtn}
          leftIcon={<QueueHeadsetFill24Icon />}
        >
          {t("Logg på")}
        </DropsButton>
        {oidcEnabled && (
          <DropsButton
            size="md"
            variant="tertiary"
            fontWeight="bold"
            type="button"
            onClick={logout}
          >
            {t("Logg ut")}
          </DropsButton>
        )}
      </HorizontalContainer>
    );
  }

  return (
    <HorizontalContainer onSubmit={logOff}>
      <ProfileDetails>
        <Text>{currentProfile?.name}</Text>
        <Text>
          (
          {removePhoneNumberPrefix(
            currentProfile.phoneNumber,
            phoneNumberPrefix,
          )}
          )
        </Text>
      </ProfileDetails>
      <DropsButton
        size="md"
        variant="secondary"
        type="submit"
        leftIcon={<QueueHeadsetFill24Icon />}
      >
        {t("Logg av")}
      </DropsButton>
      {oidcEnabled && (
        <DropsButton
          size="md"
          variant="tertiary"
          fontWeight="bold"
          type="button"
          onClick={logout}
        >
          {t("Logg ut")}
        </DropsButton>
      )}
    </HorizontalContainer>
  );
};

export default Logon;
